import React, { useState, useMemo } from 'react';
import { Edit2, Plus, BookOpen, Target, Users, Lightbulb, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { FeatureCard } from './FeatureCard';
import { CategoryEditModal } from './CategoryEditModal';
import { grammarService } from '../../services/grammarService';

interface CategoryViewProps {
  category: any;
  features: any[];
  loadingFeatures: boolean;
  onFeatureEdit: (feature: any) => void;
  onFeatureDelete: (featureId: string) => void;
  onCategoryUpdate: () => void;
}

export function CategoryView({ 
  category, 
  features, 
  loadingFeatures,
  onFeatureEdit, 
  onFeatureDelete,
  onCategoryUpdate 
}: CategoryViewProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Calculate actual subcategory count from features
  const actualSubcategoryCount = useMemo(() => {
    const subcategories = new Set(features.map(f => f.sub_category).filter(Boolean));
    return subcategories.size;
  }, [features]);

  const getComplexityColor = (score: number) => {
    if (score <= 3) return 'text-green-600';
    if (score <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleCategoryUpdate = async (updatedData: any) => {
    try {
      setSaving(true);
      await grammarService.updateCategoryMetadata(category.name, updatedData);
      setShowEditModal(false);
      onCategoryUpdate(); // Refresh categories
    } catch (error) {
      console.error('Error updating category:', error);
      // Error handling is done in the parent component
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 bg-background border-b border-border">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">{category.display_name}</h1>
                <p className="text-muted-foreground mb-4 max-w-2xl">
                  {category.description || 'No description available'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {category.linguistic_type && (
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                      {category.linguistic_type}
                    </Badge>
                  )}
                  {category.cefr_coverage?.core_level && (
                    <Badge variant="outline">
                      CEFR: {category.cefr_coverage.core_level}
                    </Badge>
                  )}
                  <Badge variant="outline">
                    {category.featureCount || features.length || 0} features
                  </Badge>
                  {!loadingFeatures && actualSubcategoryCount > 0 && (
                    <Badge variant="outline">
                      {actualSubcategoryCount} {actualSubcategoryCount === 1 ? 'subcategory' : 'subcategories'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setShowEditModal(true)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Category
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* Category Details Grid */}
          {(category.complexity_range || category.cefr_coverage || category.business_relevance) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Complexity Range */}
              {category.complexity_range && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-teal-600" />
                      Complexity Range
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Minimum:</span>
                        <span className={getComplexityColor(category.complexity_range.min || 0)}>
                          {category.complexity_range.min || 0}/10
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Maximum:</span>
                        <span className={getComplexityColor(category.complexity_range.max || 0)}>
                          {category.complexity_range.max || 0}/10
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Average:</span>
                        <span className={`font-medium ${getComplexityColor(category.complexity_range.average || 0)}`}>
                          {category.complexity_range.average || 0}/10
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* CEFR Coverage */}
              {category.cefr_coverage && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">CEFR Coverage</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {category.cefr_coverage.core_level && (
                      <div>
                        <span className="text-muted-foreground">Core Level:</span>
                        <Badge variant="secondary" className="ml-2">{category.cefr_coverage.core_level}</Badge>
                      </div>
                    )}
                    {category.cefr_coverage.introduction_range && (
                      <div>
                        <span className="text-muted-foreground">Introduction:</span>
                        <div className="flex gap-1 mt-1">
                          {category.cefr_coverage.introduction_range.map((level: string) => (
                            <Badge key={level} variant="outline" className="text-xs">
                              {level}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {category.cefr_coverage.mastery_range && (
                      <div>
                        <span className="text-muted-foreground">Mastery:</span>
                        <div className="flex gap-1 mt-1">
                          {category.cefr_coverage.mastery_range.map((level: string) => (
                            <Badge key={level} variant="outline" className="text-xs">
                              {level}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Business Relevance */}
              {category.business_relevance && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      Business Relevance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {category.business_relevance.importance && (
                      <div>
                        <span className="text-muted-foreground">Importance:</span>
                        <Badge 
                          variant="secondary" 
                          className={`ml-2 ${
                            category.business_relevance.importance === 'essential' 
                              ? 'bg-purple-100 text-purple-800' 
                              : category.business_relevance.importance === 'high' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-teal-100 text-teal-800'
                          }`}
                        >
                          {category.business_relevance.importance}
                        </Badge>
                      </div>
                    )}
                    {category.business_relevance.example_use_case && (
                      <p className="text-muted-foreground italic">
                        "{category.business_relevance.example_use_case}"
                      </p>
                    )}
                    {category.business_relevance.key_contexts && (
                      <div className="flex flex-wrap gap-1">
                        {category.business_relevance.key_contexts.map((context: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {context.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Common Challenges */}
          {category.common_challenges && category.common_challenges.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600" />
                  Common Challenges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {category.common_challenges.map((challenge: any, idx: number) => (
                    <div key={idx} className="border-l-4 border-orange-200 pl-4 py-2">
                      <h4 className="font-medium text-sm">{challenge.issue}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{challenge.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Teaching Approach */}
          {category.teaching_approach && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-base">Teaching Approach</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {category.teaching_approach.recommended_method && (
                  <div>
                    <span className="text-sm font-medium">Recommended Method:</span>
                    <Badge variant="outline" className="ml-2">
                      {category.teaching_approach.recommended_method}
                    </Badge>
                  </div>
                )}
                {category.teaching_approach.key_concepts && category.teaching_approach.key_concepts.length > 0 && (
                  <div>
                    <span className="text-sm font-medium">Key Concepts:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {category.teaching_approach.key_concepts.map((concept: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {concept}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {category.teaching_approach.practice_focus && category.teaching_approach.practice_focus.length > 0 && (
                  <div>
                    <span className="text-sm font-medium">Practice Focus:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {category.teaching_approach.practice_focus.map((focus: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {focus}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Features Section */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Grammar Features
              </h2>
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </div>

            <Separator className="mb-6" />

            {/* Features List */}
            {loadingFeatures ? (
              <Card>
                <CardContent className="py-8">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
                    <span className="ml-3 text-muted-foreground">Loading features...</span>
                  </div>
                </CardContent>
              </Card>
            ) : features.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No features found in this category.</p>
                  <Button variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Feature
                  </Button>
                </CardContent>
              </Card>
            ) : (() => {
              // Group features by subcategory
              const groupedFeatures = features.reduce((acc, feature) => {
                const subCategory = feature.sub_category || 'Uncategorized';
                if (!acc[subCategory]) {
                  acc[subCategory] = [];
                }
                acc[subCategory].push(feature);
                return acc;
              }, {} as Record<string, any[]>);

              const sortedSubCategories = Object.keys(groupedFeatures).sort((a, b) => {
                // Put 'Uncategorized' at the end
                if (a === 'Uncategorized') return 1;
                if (b === 'Uncategorized') return -1;
                return a.localeCompare(b);
              });

              return (
                <div className="space-y-6">
                  {sortedSubCategories.map((subCategory) => (
                    <SubcategorySection
                      key={subCategory}
                      subCategory={subCategory}
                      features={groupedFeatures[subCategory]}
                      onFeatureEdit={onFeatureEdit}
                      onFeatureDelete={onFeatureDelete}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Category Edit Modal */}
      {showEditModal && (
        <CategoryEditModal
          category={category}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleCategoryUpdate}
          saving={saving}
        />
      )}
    </div>
  );
}

// Subcategory Section Component
function SubcategorySection({ 
  subCategory, 
  features, 
  onFeatureEdit, 
  onFeatureDelete 
}: {
  subCategory: string;
  features: any[];
  onFeatureEdit: (feature: any) => void;
  onFeatureDelete: (featureId: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
          <h3 className="font-medium">{subCategory}</h3>
          <Badge variant="secondary" className="ml-2">
            {features.length} {features.length === 1 ? 'feature' : 'features'}
          </Badge>
        </div>
      </button>
      
      {isExpanded && (
        <div className="p-4 space-y-4 bg-background">
          {features.map((feature) => (
            <FeatureCard
              key={feature.feature_id}
              feature={feature}
              onEdit={() => onFeatureEdit(feature)}
              onDelete={() => onFeatureDelete(feature.feature_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}